/**
 * Simple Schema Validator with Fallback Mechanisms
 *
 * Provides runtime validation for API responses with intelligent fallbacks
 * to prevent app crashes when backend schema changes.
 */

import { getPropertyType, getValueType, hasProperty } from "../../helper/typeCheck";

export interface ValidationResult<T> {
  /** Validated and transformed data */
  data: T;
  /** Validation warnings */
  warnings: string[];
  /** Validation errors */
  errors: string[];
  /** Whether validation passed (no errors) */
  isValid: boolean;
  /** Fields that were fixed with defaults */
  fixedFields: string[];
}

/**
 * Simple schema validator that validates data against a default template
 */
export class SchemaValidator {
  private validatorName: string;
  private maxDepth: number;

  public constructor(validatorName: string, maxDepth: number) {
    this.validatorName = validatorName;
    this.maxDepth = maxDepth;
  }

  /**
   * Validates data against a default template object
   * @param data - The data to validate
   * @param defaultData - The default data template to validate against
   * @returns Validation result with fixed data
   */
  public validate = <T>(data: unknown, defaultData: T): ValidationResult<T> => {
    const valueType = getValueType(data);
    // Handle null/undefined data
    if (valueType === "null" || valueType === "undefined") {
      return {
        data: defaultData,
        warnings: [`${this.validatorName}: Data is null or undefined, using default`],
        errors: [],
        isValid: true,
        fixedFields: [],
      };
    }

    // Handle non-object data
    if (valueType !== "object") {
      return {
        data: defaultData,
        warnings: [
          `${this.validatorName}: Expected object, got ${Array.isArray(data) ? "array" : typeof data}, using default`,
        ],
        errors: [
          `${this.validatorName}: Expected object, got ${Array.isArray(data) ? "array" : typeof data}, using default`,
        ],
        isValid: true,
        fixedFields: [],
      };
    }

    return this.validateObject(data as Record<string, unknown>, defaultData, 0);
  };

  /**
   * Validates an array of items against a default template
   * @param data - The array to validate
   * @param defaultData - The default data template for each item
   * @param depth - Current recursion depth
   * @returns Validation result with fixed data
   */
  public validateArray<T>(data: unknown, defaultData: T, depth = 0): ValidationResult<T[]> {
    const warnings: string[] = [];
    const errors: string[] = [];
    const fixedFields: string[] = [];
    const result: T[] = [];

    if (!Array.isArray(data)) {
      errors.push(`${this.validatorName}: Expected array, got ${typeof data}`);
      return {
        data: [],
        warnings,
        errors,
        isValid: false,
        fixedFields,
      };
    }

    // Check if we've reached the max depth
    if (depth >= this.maxDepth) {
      warnings.push(`${this.validatorName}: Max depth reached (${this.maxDepth}), skipping deeper validation`);
      return {
        data: data as T[],
        warnings,
        errors,
        isValid: true,
        fixedFields,
      };
    }

    for (const item of data) {
      const validationResult = this.validateObject(item as Record<string, unknown>, defaultData, depth + 1);
      result.push(validationResult.data);
      warnings.push(...validationResult.warnings);
      errors.push(...validationResult.errors);
      fixedFields.push(...validationResult.fixedFields);
    }

    return {
      data: result,
      warnings,
      errors,
      isValid: errors.length === 0,
      fixedFields,
    };
  }

  /**
   * Validates an object against a default template
   * @param data - The object to validate
   * @param defaultData - The default data template
   * @param depth - Current recursion depth
   * @returns Validation result with fixed data
   */
  private validateObject<T>(data: Record<string, unknown>, defaultData: T, depth = 0): ValidationResult<T> {
    const warnings: string[] = [];
    const errors: string[] = [];
    const fixedFields: string[] = [];
    const result = { ...data } as Record<string, unknown>;

    // Check if we've reached the max depth
    if (depth >= this.maxDepth) {
      warnings.push(`${this.validatorName}: Max depth reached (${this.maxDepth}), skipping deeper validation`);
      return {
        data: data as T,
        warnings,
        errors,
        isValid: true,
        fixedFields,
      };
    }

    // Validate each field in the default template
    for (const [fieldName, defaultValue] of Object.entries(defaultData as Record<string, unknown>)) {
      const fieldValidation = this.validateField(data, fieldName, defaultValue, depth);

      result[fieldName] = fieldValidation.value;
      warnings.push(...fieldValidation.warnings);
      errors.push(...fieldValidation.errors);

      if (fieldValidation.wasFixed) {
        fixedFields.push(fieldName);
      }
    }

    return {
      data: result as T,
      warnings,
      errors,
      isValid: errors.length === 0,
      fixedFields,
    };
  }

  /**
   * Validates a field against an expected type and default value
   * @param data - The object containing the field
   * @param fieldName - The name of the field to validate
   * @param defaultValue - The default value for the field
   * @param depth - Current recursion depth
   * @returns Validation result for the field
   */
  private validateField(
    data: Record<string, unknown>,
    fieldName: string,
    defaultValue: unknown,
    depth = 0
  ): {
    value: unknown;
    warnings: string[];
    errors: string[];
    wasFixed: boolean;
  } {
    const warnings: string[] = [];
    const errors: string[] = [];
    const wasFixed = false;

    // Check if field exists
    if (!hasProperty(data, fieldName)) {
      warnings.push(`${this.validatorName}: Field '${fieldName}' is missing, using default value`);
      return {
        value: defaultValue,
        warnings,
        errors,
        wasFixed: true,
      };
    }

    const fieldValue = data[fieldName];
    const actualType = getPropertyType(data, fieldName);
    const expectedType = getPropertyType({ default: defaultValue }, "default");

    // Type mismatch check
    if (actualType !== expectedType && actualType !== "undefined" && actualType !== "null") {
      warnings.push(
        `${this.validatorName}: Field '${fieldName}' has type '${actualType}', expected '${expectedType}', using default value`
      );
      return {
        value: defaultValue,
        warnings,
        errors,
        wasFixed: true,
      };
    }

    // Check max depth for nested structures
    if (depth + 1 >= this.maxDepth && (expectedType === "object" || expectedType === "array")) {
      warnings.push(
        `${this.validatorName}: Max depth reached (${this.maxDepth}) for field '${fieldName}', skipping deeper validation`
      );
      return {
        value: fieldValue,
        warnings,
        errors,
        wasFixed: false,
      };
    }

    // Handle nested objects
    if (expectedType === "object" && !Array.isArray(defaultValue) && !Array.isArray(fieldValue)) {
      const nestedResult = this.validateObject(
        fieldValue as Record<string, unknown>,
        defaultValue as Record<string, unknown>,
        depth + 1
      );

      warnings.push(...nestedResult.warnings.map((warning) => `${fieldName}.${warning}`));
      errors.push(...nestedResult.errors.map((error) => `${fieldName}.${error}`));

      return {
        value: nestedResult.data,
        warnings,
        errors,
        wasFixed: nestedResult.fixedFields.length > 0,
      };
    }

    // Handle arrays with validation
    if (expectedType === "array" && Array.isArray(defaultValue) && Array.isArray(fieldValue)) {
      const arrayResult = this.validateArrayField(fieldValue, defaultValue, fieldName, depth);
      warnings.push(...arrayResult.warnings);
      errors.push(...arrayResult.errors);
      return arrayResult;
    }

    // Value is valid
    return {
      value: fieldValue,
      warnings,
      errors,
      wasFixed,
    };
  }

  /**
   * Validates an array field
   * @param fieldValue - The array value to validate
   * @param defaultValue - The default array value
   * @param fieldName - The name of the field
   * @param depth - Current recursion depth
   * @returns Validation result for the field
   */
  private validateArrayField(
    fieldValue: unknown[],
    defaultValue: unknown[],
    fieldName: string,
    depth: number
  ): {
    value: unknown;
    warnings: string[];
    errors: string[];
    wasFixed: boolean;
  } {
    const warnings: string[] = [];
    const errors: string[] = [];
    // If default array has items, use first item as template for validation
    if (defaultValue.length > 0 && depth + 1 < this.maxDepth) {
      const templateItem = defaultValue[0];
      const arrayValidation = this.validateArray(fieldValue, templateItem, depth + 1);

      warnings.push(...arrayValidation.warnings.map((warning) => `${fieldName}.${warning}`));
      errors.push(...arrayValidation.errors.map((error) => `${fieldName}.${error}`));

      return {
        value: arrayValidation.data,
        warnings,
        errors,
        wasFixed: arrayValidation.fixedFields.length > 0,
      };
    }

    // Simple array without template or max depth reached
    return {
      value: fieldValue,
      warnings,
      errors,
      wasFixed: false,
    };
  }
}
